// src/utils/mopsReportPdf.js
//
// Single-student MOPs report as a PDF.
//
// IMPERATIVE jsPDF ONLY. Do not reach for html2canvas or doc.html() here —
// both render blank in this app (offscreen rendering), which is why every
// measurement below is placed by hand.

import { jsPDF } from 'jspdf';

const KA = {
  navy: [35, 60, 95],
  red: [232, 53, 48],
  gold: [162, 145, 100],
  ink: [26, 26, 26],
  muted: [107, 114, 128],
  line: [222, 226, 230],
  wash: [248, 248, 247],
  white: [255, 255, 255],
};

const PAGE = { w: 210, h: 297, ml: 16, mr: 16, mt: 16, mb: 18 };
const CONTENT_W = PAGE.w - PAGE.ml - PAGE.mr;

/**
 * Thin wrapper over jsPDF that tracks the cursor and breaks pages.
 * Everything is placed explicitly; nothing is inferred from a DOM.
 */
class PDFDoc {
  constructor() {
    this.doc = new jsPDF({ unit: 'mm', format: 'a4' });
    this.y = PAGE.mt;
    this.page = 1;
  }

  /** Reserve `h` mm; start a new page if it would overflow the bottom margin. */
  need(h) {
    if (this.y + h > PAGE.h - PAGE.mb) {
      this.doc.addPage();
      this.page += 1;
      this.y = PAGE.mt;
      return true;
    }
    return false;
  }

  font(size, style = 'normal', color = KA.ink) {
    this.doc.setFont('helvetica', style);
    this.doc.setFontSize(size);
    this.doc.setTextColor(...color);
    return this;
  }

  text(str, x, opts = {}) {
    this.doc.text(String(str == null ? '' : str), x, this.y, opts);
    return this;
  }

  /** Text clipped to a width, so long names never bleed into the next column. */
  clipped(str, x, maxW, opts = {}) {
    const s = String(str == null ? '' : str);
    let out = s;
    while (out.length > 1 && this.doc.getTextWidth(out) > maxW) {
      out = out.slice(0, -1);
    }
    if (out !== s) out = out.slice(0, -1) + '…';
    this.doc.text(out, x, this.y, opts);
    return this;
  }

  rect(x, y, w, h, color, style = 'F') {
    if (style === 'F') this.doc.setFillColor(...color);
    else this.doc.setDrawColor(...color);
    this.doc.rect(x, y, w, h, style);
    return this;
  }

  hr(color = KA.line, weight = 0.2) {
    this.doc.setDrawColor(...color);
    this.doc.setLineWidth(weight);
    this.doc.line(PAGE.ml, this.y, PAGE.w - PAGE.mr, this.y);
    this.doc.setLineWidth(0.2);
    return this;
  }

  gap(mm) {
    this.y += mm;
    return this;
  }
}

const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

function bandFor(total, thresholds) {
  if (!Array.isArray(thresholds) || !thresholds.length) return null;
  return (
    thresholds
      .filter((t) => Number(total) >= Number(t.points))
      .sort((a, b) => Number(b.points) - Number(a.points))[0] || null
  );
}

function strikeBandFor(strikes, rungs) {
  if (!Array.isArray(rungs) || !rungs.length) return null;
  return (
    rungs
      .filter((r) => Number(strikes) >= Number(r.strikes))
      .sort((a, b) => Number(b.strikes) - Number(a.strikes))[0] || null
  );
}

/* ------------------------------------------------------------------ */

/**
 * Build the report. Returns the jsPDF instance so the caller can save(),
 * open a blob URL, or hand it to a print dialog.
 */
export function buildStudentReport({
  student,
  events = [],
  season = {},
  thresholds = [],
  phoneRungs = [],
  generatedBy = null,
}) {
  const p = new PDFDoc();
  const d = p.doc;

  const total = Number(student.total) || 0;
  const band = bandFor(total, thresholds);
  const strikeBand = strikeBandFor(student.phoneStrikes, phoneRungs);

  /* ---------- header ---------- */
  p.rect(0, 0, PAGE.w, 26, KA.navy);
  p.y = 11;
  p.font(15, 'bold', KA.white).text("King's Academy", PAGE.ml);
  p.y = 18;
  p.font(9.5, 'normal', [210, 218, 230]).text(
    'Missed Obligation Points — Student Report',
    PAGE.ml
  );
  p.font(9, 'normal', [210, 218, 230]);
  d.text(season.label || season.id || '', PAGE.w - PAGE.mr, 18, { align: 'right' });
  p.y = 36;

  /* ---------- student identity ---------- */
  p.font(17, 'bold', KA.navy).clipped(student.person || `Student ${student.personId}`, PAGE.ml, CONTENT_W);
  p.gap(6);
  const meta = [
    `ID ${student.personId}`,
    student.grade != null ? `Grade ${student.grade}` : null,
    student.advisorName ? `Advisor: ${student.advisorName}` : null,
  ]
    .filter(Boolean)
    .join('   ·   ');
  p.font(9.5, 'normal', KA.muted).clipped(meta, PAGE.ml, CONTENT_W);
  p.gap(6);
  p.hr(KA.navy, 0.6);
  p.gap(8);

  /* ---------- standing ---------- */
  if (band) {
    p.need(16);
    p.rect(PAGE.ml, p.y - 4.5, CONTENT_W, 11, [253, 240, 239]);
    p.rect(PAGE.ml, p.y - 4.5, 1.4, 11, KA.red);
    p.font(10.5, 'bold', KA.red).text(`Current standing: ${band.label}`, PAGE.ml + 4);
    p.font(9, 'normal', KA.muted);
    d.text(`reached at ${band.points} points`, PAGE.w - PAGE.mr - 2, p.y, { align: 'right' });
    p.gap(12);
  }

  /* ---------- summary tiles ---------- */
  const tiles = [
    ['Total MOPs', total, KA.red],
    ['Tardies', Number(student.tardyPoints) || 0, KA.navy],
    ['Phone', Number(student.phonePoints) || 0, KA.navy],
    ['Other', Number(student.manualPoints) || 0, KA.navy],
    ['Phone strikes', Number(student.phoneStrikes) || 0, strikeBand ? KA.gold : KA.navy],
  ];
  p.need(20);
  const tileW = CONTENT_W / tiles.length;
  const tileTop = p.y;
  tiles.forEach(([label, value, color], i) => {
    const x = PAGE.ml + i * tileW;
    p.y = tileTop + 6;
    p.font(15, 'bold', color).text(String(value), x);
    p.y = tileTop + 11;
    p.font(7.5, 'normal', KA.muted).text(String(label).toUpperCase(), x);
  });
  p.y = tileTop + 19;

  if (strikeBand) {
    p.font(8.5, 'italic', KA.gold).text(`Phone ladder: ${strikeBand.label}`, PAGE.ml);
    p.gap(6);
  }
  p.gap(3);
  p.hr();
  p.gap(7);

  /* ---------- event table ---------- */
  p.font(11, 'bold', KA.navy).text(`Record of points (${events.length})`, PAGE.ml);
  p.gap(6);

  const cols = [
    { k: 'date', label: 'Date', w: 22 },
    { k: 'type', label: 'Type', w: 24 },
    { k: 'detail', label: 'Detail', w: 46 },
    { k: 'where', label: 'Block', w: 20 },
    { k: 'by', label: 'Reported by', w: 44 },
    { k: 'pts', label: 'Pts', w: 12, align: 'right' },
  ];
  const colX = [];
  let cx = PAGE.ml;
  cols.forEach((c) => {
    colX.push(cx);
    cx += c.w;
  });

  const header = () => {
    p.rect(PAGE.ml, p.y - 3.6, CONTENT_W, 6.5, KA.wash);
    p.font(7.5, 'bold', KA.muted);
    cols.forEach((c, i) => {
      const x = c.align === 'right' ? colX[i] + c.w - 1 : colX[i] + 1;
      d.text(c.label.toUpperCase(), x, p.y, c.align === 'right' ? { align: 'right' } : {});
    });
    p.gap(6.5);
  };
  header();

  if (!events.length) {
    p.font(9, 'italic', KA.muted).text('No points recorded this term.', PAGE.ml + 1);
    p.gap(6);
  }

  const sorted = [...events].sort((a, b) => String(b.date).localeCompare(String(a.date)));
  sorted.forEach((e, idx) => {
    if (p.need(7)) header();
    if (idx % 2 === 1) p.rect(PAGE.ml, p.y - 3.4, CONTENT_W, 6, [252, 252, 252]);

    const typeLabel =
      e.source === 'tardy' ? 'Tardy' : e.source === 'phone' ? 'Phone' : e.category || 'Other';
    const where =
      e.block || (e.slot != null ? `Slot ${e.slot}` : null) || e.station || '—';
    const row = {
      date: fmtDate(e.date),
      type: typeLabel,
      detail: e.class || e.reason || '—',
      where,
      by: e.teacherName || e.issuedBy || '—',
      pts: String(e.points ?? 1),
    };

    cols.forEach((c, i) => {
      const isPts = c.k === 'pts';
      p.font(8, isPts ? 'bold' : 'normal', isPts ? KA.ink : KA.muted);
      if (c.k === 'date' || c.k === 'type') p.font(8, 'normal', KA.ink);
      const x = c.align === 'right' ? colX[i] + c.w - 1 : colX[i] + 1;
      if (c.align === 'right') d.text(row[c.k], x, p.y, { align: 'right' });
      else p.clipped(row[c.k], x, c.w - 4);
    });
    p.gap(6);
  });

  /* ---------- totals ---------- */
  p.need(10);
  p.gap(1);
  p.hr(KA.navy, 0.4);
  p.gap(5.5);
  p.font(9.5, 'bold', KA.navy).text('Total', colX[0] + 1);
  d.text(String(total), PAGE.w - PAGE.mr - 1, p.y, { align: 'right' });

  /* ---------- footer on every page ---------- */
  const pages = d.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    d.setPage(i);
    d.setDrawColor(...KA.line);
    d.line(PAGE.ml, PAGE.h - 13, PAGE.w - PAGE.mr, PAGE.h - 13);
    d.setFont('helvetica', 'normal');
    d.setFontSize(7.5);
    d.setTextColor(...KA.muted);
    const stamp =
      `Generated ${new Date().toLocaleString('en-GB')}` +
      (generatedBy ? ` by ${generatedBy}` : '');
    d.text(stamp, PAGE.ml, PAGE.h - 9);
    d.text(`Page ${i} of ${pages}`, PAGE.w - PAGE.mr, PAGE.h - 9, { align: 'right' });
    d.text('Confidential — student disciplinary record', PAGE.ml, PAGE.h - 5.5);
  }

  return d;
}

/* ------------------------------------------------------------------ */
/* Tardies by teacher                                                   */
/* ------------------------------------------------------------------ */

/**
 * The teacher breakdown as a PDF, in whatever order the screen showed it —
 * the caller passes its already-sorted rows so the paper matches what was
 * on screen when the button was pressed.
 *
 * Same imperative jsPDF approach as the student report: no html2canvas,
 * no doc.html(), every element placed by hand.
 */
export function buildTeacherReport({ rows = [], season = {}, generatedBy = null, sortLabel = null }) {
  const p = new PDFDoc();
  const d = p.doc;

  const totalTardies = rows.reduce((s, r) => s + (Number(r.tardies) || 0), 0);

  /* ---------- header ---------- */
  p.rect(0, 0, PAGE.w, 26, KA.navy);
  p.y = 11;
  p.font(15, 'bold', KA.white).text("King's Academy", PAGE.ml);
  p.y = 18;
  p.font(9.5, 'normal', [210, 218, 230]).text('Unexcused Tardies by Teacher', PAGE.ml);
  p.font(9, 'normal', [210, 218, 230]);
  d.text(season.label || season.id || '', PAGE.w - PAGE.mr, 18, { align: 'right' });
  p.y = 36;

  /* ---------- summary ---------- */
  const tiles = [
    ['Tardies', totalTardies, KA.red],
    ['Teachers', rows.length, KA.navy],
    ['Avg per teacher', rows.length ? (totalTardies / rows.length).toFixed(1) : '0', KA.navy],
  ];
  const tileW = CONTENT_W / 3;
  const top = p.y;
  tiles.forEach(([label, value, color], i) => {
    const x = PAGE.ml + i * tileW;
    p.y = top + 6;
    p.font(16, 'bold', color).text(String(value), x);
    p.y = top + 11.5;
    p.font(7.5, 'normal', KA.muted).text(String(label).toUpperCase(), x);
  });
  p.y = top + 18;

  if (sortLabel) {
    p.font(8.5, 'italic', KA.muted).text(`Sorted by ${sortLabel}`, PAGE.ml);
    p.gap(5);
  }
  p.gap(2);
  p.hr(KA.navy, 0.5);
  p.gap(7);

  /* ---------- table ---------- */
  const cols = [
    { k: 'n', label: '#', w: 9, align: 'right' },
    { k: 'name', label: 'Teacher', w: 56 },
    { k: 'tardies', label: 'Tardies', w: 18, align: 'right' },
    { k: 'students', label: 'Students', w: 19, align: 'right' },
    { k: 'per', label: 'Per std', w: 17, align: 'right' },
    { k: 'blocks', label: 'Blocks', w: 36 },
    { k: 'last', label: 'Last', w: 23 },
  ];
  const colX = [];
  let cx = PAGE.ml;
  cols.forEach((c) => {
    colX.push(cx);
    cx += c.w;
  });

  const header = () => {
    p.rect(PAGE.ml, p.y - 3.6, CONTENT_W, 6.5, KA.wash);
    p.font(7.5, 'bold', KA.muted);
    cols.forEach((c, i) => {
      const x = c.align === 'right' ? colX[i] + c.w - 1 : colX[i] + 1;
      d.text(c.label.toUpperCase(), x, p.y, c.align === 'right' ? { align: 'right' } : {});
    });
    p.gap(6.5);
  };
  header();

  if (!rows.length) {
    p.font(9, 'italic', KA.muted).text('No tardies recorded this term.', PAGE.ml + 1);
    p.gap(6);
  }

  rows.forEach((r, idx) => {
    if (p.need(7)) header();
    if (idx % 2 === 1) p.rect(PAGE.ml, p.y - 3.4, CONTENT_W, 6, [252, 252, 252]);

    const blocks = Object.entries(r.blocks || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([b, n]) => `${b}:${n}`)
      .join('  ');

    const row = {
      n: String(idx + 1),
      name: r.name || '—',
      tardies: String(r.tardies ?? 0),
      students: String(r.studentCount ?? 0),
      per: String(r.tardiesPerStudent ?? 0),
      blocks: blocks || '—',
      last: r.lastDate || '—',
    };

    cols.forEach((c, i) => {
      const emphasis = c.k === 'tardies';
      p.font(8, emphasis ? 'bold' : 'normal', emphasis ? KA.red : c.k === 'name' ? KA.ink : KA.muted);
      const x = c.align === 'right' ? colX[i] + c.w - 1 : colX[i] + 1;
      if (c.align === 'right') d.text(row[c.k], x, p.y, { align: 'right' });
      else p.clipped(row[c.k], x, c.w - 4);
    });
    p.gap(6);
  });

  /* ---------- totals ---------- */
  p.need(10);
  p.gap(1);
  p.hr(KA.navy, 0.4);
  p.gap(5.5);
  p.font(9.5, 'bold', KA.navy).text('Total', colX[1] + 1);
  d.text(String(totalTardies), colX[2] + cols[2].w - 1, p.y, { align: 'right' });

  /* ---------- reading note ---------- */
  p.gap(9);
  p.need(14);
  p.font(7.5, 'italic', KA.muted);
  d.text(
    'Per std = tardies divided by distinct students. A high count across many students describes a block;',
    PAGE.ml,
    p.y
  );
  p.gap(3.6);
  d.text(
    'the same count across few students describes those students. This measures recording, not lateness.',
    PAGE.ml,
    p.y
  );

  /* ---------- footer ---------- */
  const pages = d.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    d.setPage(i);
    d.setDrawColor(...KA.line);
    d.line(PAGE.ml, PAGE.h - 13, PAGE.w - PAGE.mr, PAGE.h - 13);
    d.setFont('helvetica', 'normal');
    d.setFontSize(7.5);
    d.setTextColor(...KA.muted);
    const stamp =
      `Generated ${new Date().toLocaleString('en-GB')}` + (generatedBy ? ` by ${generatedBy}` : '');
    d.text(stamp, PAGE.ml, PAGE.h - 9);
    d.text(`Page ${i} of ${pages}`, PAGE.w - PAGE.mr, PAGE.h - 9, { align: 'right' });
    d.text('Confidential — internal use', PAGE.ml, PAGE.h - 5.5);
  }

  return d;
}

function teacherFileSlug(season) {
  const s = String(season.id || 'season').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase();
  return `tardies-by-teacher-${s}`;
}

export function downloadTeacherReport(args) {
  buildTeacherReport(args).save(`${teacherFileSlug(args.season || {})}.pdf`);
}

export function printTeacherReport(args) {
  const doc = buildTeacherReport(args);
  doc.autoPrint();
  const url = doc.output('bloburl');
  const w = window.open(url, '_blank');
  if (!w) doc.save(`${teacherFileSlug(args.season || {})}.pdf`);
}

/** Filename-safe slug of the student's name. */
function fileSlug(student) {
  const base = String(student.person || student.personId || 'student')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `mops-${base}-${student.personId}`;
}

export function downloadStudentReport(args) {
  buildStudentReport(args).save(`${fileSlug(args.student)}.pdf`);
}

/** Opens the browser print dialog (Save as PDF lives there too). */
export function printStudentReport(args) {
  const doc = buildStudentReport(args);
  doc.autoPrint();
  const url = doc.output('bloburl');
  const w = window.open(url, '_blank');
  if (!w) doc.save(`${fileSlug(args.student)}.pdf`); // popup blocked — fall back
}